/**
 * MobileGroupCollabsList
 *
 * Mobile card-based replacement for the Collabs sub-view DataTable in GroupesView.
 * Shows each collaborator as a touch-friendly card with key info,
 * grouped by team with section headers.
 */
import React, { useState, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  CardActionArea,
  TextField,
  InputAdornment,
  Typography,
  Chip,
  Divider,
  Fade,
  Pagination,
  Switch,
  FormControlLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Langs from "../../../app/lang/langs";
import { getFormattedDate } from "../../../app/utils/helpers";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)";
const PAGE_SIZE = 20;

function getInitials(collab) {
  const first = (collab.prenom?.[0] || "").toUpperCase();
  const last = (collab.nom?.[0] || "").toUpperCase();
  return `${first}${last}`;
}

function getAvatarBgColor(collab) {
  if (collab.color) return collab.color;
  return "#FF9015";
}

function getStatusColor(statut) {
  if (["Présent", "Actif"].includes(statut)) return "success";
  if (statut === "Parti") return "error";
  return "default";
}

function isActiveStatus(statut) {
  return ["Présent", "Actif"].includes(statut);
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */
export default function MobileGroupCollabsList({
  collabsList,
  getTeamLabel,
  onViewDetails,
  onAutoChange,
  onColorChange,
  canEdit,
}) {
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);

  const data = collabsList || [];

  const filteredData = useMemo(() => {
    if (!searchText.trim()) return data;
    const terms = searchText.toLowerCase().split(" ").filter(Boolean);
    return data.filter((collab) => {
      const searchStr = [collab.nom, collab.prenom, collab.matricule, collab.poste, collab.email]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return terms.every((term) => searchStr.includes(term));
    });
  }, [data, searchText]);

  const groupedData = useMemo(() => {
    const teamIds = [...new Set(filteredData.map((c) => c.id_team))];
    if (teamIds.length <= 1) {
      return [{ teamId: teamIds[0] || null, teamLabel: null, collabs: filteredData }];
    }
    return teamIds.map((teamId) => ({
      teamId,
      teamLabel: getTeamLabel ? getTeamLabel(teamId) : `Équipe ${teamId}`,
      collabs: filteredData.filter((c) => c.id_team === teamId),
    }));
  }, [filteredData, getTeamLabel]);

  const totalCount = filteredData.length;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const startIdx = (page - 1) * PAGE_SIZE;
  const endIdx = page * PAGE_SIZE;

  const paginatedGroups = useMemo(() => {
    let idx = 0;
    const result = [];
    for (const group of groupedData) {
      const groupStart = idx;
      const groupEnd = idx + group.collabs.length;
      if (groupEnd > startIdx && groupStart < endIdx) {
        const sliceStart = Math.max(0, startIdx - groupStart);
        const sliceEnd = Math.min(group.collabs.length, endIdx - groupStart);
        result.push({ ...group, collabs: group.collabs.slice(sliceStart, sliceEnd) });
      }
      idx = groupEnd;
    }
    return result;
  }, [groupedData, startIdx, endIdx]);

  React.useEffect(() => {
    setPage(1);
  }, [searchText]);

  return (
    <Box
      sx={{
        px: 2,
        pt: 1.5,
        pb: 2.5,
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Search bar */}
      <TextField
        fullWidth
        size="small"
        placeholder="Rechercher..."
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
        sx={{
          mb: 1.5,
          "& input": { fontSize: "16px" },
          "& .MuiOutlinedInput-root": {
            borderRadius: "14px",
            bgcolor: "rgba(255,255,255,0.75)",
            backdropFilter: "blur(12px)",
            transition: TRANSITION_SMOOTH,
            "& fieldset": { borderColor: "rgba(0,0,0,0.06)", transition: TRANSITION_SMOOTH },
            "&:hover fieldset": { borderColor: "rgba(0,0,0,0.15)" },
            "&.Mui-focused fieldset": { borderColor: "#FF9015", boxShadow: "0 0 0 3px rgba(255,144,21,0.15)" },
          },
        }}
      />

      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, px: 0.5, fontWeight: 500, letterSpacing: "0.02em", opacity: 0.7 }}>
        {totalCount} résultat{totalCount !== 1 ? "s" : ""}
      </Typography>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          pb: 2,
          scrollBehavior: "smooth",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": { background: "rgba(0,0,0,0.12)", borderRadius: 3 },
        }}
      >
        {paginatedGroups.map((group) => (
          <React.Fragment key={group.teamId || "all"}>
            {/* Team section header */}
            {group.teamLabel && (
              <Box
                sx={{
                  px: 1.5, py: 1, mb: 1, mt: 0.5,
                  borderRadius: "12px",
                  bgcolor: "rgba(255,144,21,0.07)",
                  borderLeft: "3px solid #FF9015",
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#FF9015" }}>
                  {group.teamLabel}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                  {group.collabs.length} collaborateur{group.collabs.length !== 1 ? "s" : ""}
                </Typography>
              </Box>
            )}

            {group.collabs.map((collab, index) => (
              <Fade in key={collab.id} timeout={200 + index * 40}>
                <Card
                  elevation={0}
                  sx={{
                    mb: 1.5,
                    borderRadius: "16px",
                    overflow: "hidden",
                    bgcolor: "rgba(255,255,255,0.85)",
                    backdropFilter: "blur(16px)",
                    border: "1px solid rgba(0,0,0,0.06)",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.06)",
                    transition: "transform 250ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:active": { transform: "scale(0.98)", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" },
                  }}
                >
                  <CardActionArea onClick={() => onViewDetails && onViewDetails(collab)} sx={{ p: 0 }}>
                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                      {/* Top row: avatar + name + status */}
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: 1 }}>
                          <Box
                            sx={{
                              width: 40, height: 40, borderRadius: "50%",
                              bgcolor: getAvatarBgColor(collab),
                              color: "white",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              fontWeight: 700, fontSize: "0.85rem", flexShrink: 0, position: "relative",
                            }}
                          >
                            {getInitials(collab)}
                            {collab.color && (
                              <Box sx={{ position: "absolute", bottom: -1, right: -1, width: 12, height: 12, borderRadius: "50%", bgcolor: collab.color, border: "2px solid white" }} />
                            )}
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.9rem", lineHeight: 1.3, letterSpacing: "-0.01em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {collab.prenom} {collab.nom}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", opacity: 0.75 }}>
                              {collab.matricule}{collab.poste ? ` · ${collab.poste}` : ""}
                            </Typography>
                          </Box>
                        </Box>
                        {collab.statut && (
                          <Chip
                            label={collab.statut}
                            size="small"
                            color={getStatusColor(collab.statut)}
                            variant={isActiveStatus(collab.statut) ? "filled" : "outlined"}
                            sx={{ fontWeight: 600, fontSize: "0.7rem", height: 24, borderRadius: "8px", ml: 1, flexShrink: 0 }}
                          />
                        )}
                      </Box>

                      {/* Info chips: entry date, CP balance */}
                      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mt: 0.75 }}>
                        {collab.entryDate && (
                          <Chip
                            icon={<InfoOutlinedIcon sx={{ fontSize: "0.8rem" }} />}
                            label={getFormattedDate(collab.entryDate)}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: "0.7rem", height: 24, borderRadius: "8px", transition: TRANSITION_SMOOTH }}
                          />
                        )}
                        {collab.solde_cp_current != null && (
                          <Chip
                            label={`CP N : ${collab.solde_cp_current}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: "0.7rem", height: 24, borderRadius: "8px", fontWeight: 600, transition: TRANSITION_SMOOTH }}
                          />
                        )}
                        {!group.teamLabel && getTeamLabel && collab.id_team && (
                          <Chip
                            label={getTeamLabel(collab.id_team)}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: "0.7rem", height: 24, maxWidth: 140, borderRadius: "8px", transition: TRANSITION_SMOOTH }}
                          />
                        )}
                      </Box>
                    </CardContent>
                  </CardActionArea>

                  {/* AT switch — only when edit permission */}
                  {canEdit && canEdit(collab.id_team) && (
                    <>
                      <Divider sx={{ opacity: 0.4, borderColor: "rgba(0,0,0,0.06)" }} />
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 0.5 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={!!collab.auto}
                              onChange={(e) => { e.stopPropagation(); onAutoChange && onAutoChange(collab.id, e.target.checked); }}
                              size="small"
                              sx={{
                                "& .MuiSwitch-switchBase.Mui-checked": { color: "#FF9015" },
                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#FF9015" },
                              }}
                            />
                          }
                          label={<Typography variant="caption" sx={{ fontSize: "0.75rem", opacity: 0.8 }}>AT</Typography>}
                          sx={{ m: 0 }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </Box>
                    </>
                  )}
                </Card>
              </Fade>
            ))}
          </React.Fragment>
        ))}

        {filteredData.length === 0 && (
          <Fade in timeout={400}>
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.5 }}>
                Aucun collaborateur trouvé
              </Typography>
            </Box>
          </Fade>
        )}
      </Box>

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_e, value) => setPage(value)}
            size="small"
            sx={{
              "& .MuiPaginationItem-root": {
                borderRadius: "10px",
                transition: TRANSITION_SMOOTH,
                "&:hover": { transform: "scale(1.08)" },
                "&:active": { transform: "scale(0.95)" },
                "&.Mui-selected": {
                  bgcolor: "#FF9015",
                  color: "white",
                  boxShadow: "0 2px 8px rgba(255,144,21,0.35)",
                  "&:hover": { bgcolor: "#e6820d" },
                },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
}
